import { afterNextRender, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '../util/storage.service';
import { CONST } from '../util/const';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, Observable, interval, Subscription, firstValueFrom } from 'rxjs';
import { UserRoles, UserStatus } from '../core/state/users/models';
import { ToastService } from '@black/ui';
import { Store } from '@ngrx/store';
import { State } from '../core/state';
import * as UserActions from '../core/state/users/user.actions';
import { UserService } from '../core/state/users/users.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storage = inject(StorageService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private store = inject<Store<State>>(Store);
  private userService = inject(UserService);

  // Private variable to store authentication state
  private _isAuthenticated = new BehaviorSubject<boolean>(false);

  // Deduplicate concurrent token refresh calls
  private refreshPromise: Promise<string | null> | null = null;

  // Observable that components can subscribe to for auth state changes
  public isAuthenticated$: Observable<boolean> =
    this._isAuthenticated.asObservable();

  // Private variable to store user data
  private _userData: any = null;

  // Token expiration check subscription
  private _tokenCheckSubscription: Subscription | null = null;

  // Token expiration warning threshold (in seconds)
  private readonly TOKEN_EXPIRATION_WARNING_THRESHOLD = 600; // 10 minutes

  // Flag to track if we've already shown the expiration warning
  private _hasShownExpirationWarning = false;

  constructor() {
    // Initialize authentication state when service is created
    this.initializeAuthState();
    // Delay token expiration check until after first render for efficiency
    afterNextRender(() => {
      this.startTokenExpirationCheck();
    });
  }

  /**
   * Start checking for token expiration
   * Checks every minute to proactively renew tokens before they expire
   */
  private startTokenExpirationCheck(): void {
    if (this._tokenCheckSubscription) {
      this._tokenCheckSubscription.unsubscribe();
    }

    this._tokenCheckSubscription = interval(60 * 1000).subscribe(() => {
      this.checkTokenExpiration();
    });
  }

  /**
   * Refresh the access token using the stored refresh token.
   * Concurrent invocations share the same pending Promise.
   * @returns The new access token or null if refresh failed
   */
  public async refreshToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        const refreshToken = await this.getRefreshToken();
        if (!refreshToken) return null;

        const response = await firstValueFrom(
          this.userService.refreshToken(refreshToken),
        );
        if (response?.token) {
          this.setAuthState(response.token, response.refreshToken, true);
          return response.token;
        }
        return null;
      } catch {
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Check if the token is expired or about to expire.
   * Proactively refreshes the token when within the warning threshold.
   * @returns true if token is valid, false if expired and refresh failed
   */
  private async checkTokenExpiration(): Promise<boolean> {
    const authToken = await this.getToken();

    if (!authToken) {
      if (await this.refreshToken()) return true;
      this._isAuthenticated.next(false);
      this._userData = null;
      return false;
    }

    try {
      const decodedToken = jwtDecode<any>(authToken);
      const timeUntilExpiration = decodedToken.exp - Date.now() / 1000;

      // If token is expired, attempt refresh before forcing logout
      if (timeUntilExpiration <= 0) {
        if (await this.refreshToken()) return true;

        this._isAuthenticated.next(false);
        this._userData = null;
        this.toastService.showToast(
          'Your session has expired. Please log in again.',
          'error',
        );
        this.store.dispatch(UserActions.logout({ redirect: true }));
        return false;
      }

      // If token is about to expire, proactively refresh
      if (timeUntilExpiration <= this.TOKEN_EXPIRATION_WARNING_THRESHOLD) {
        if (await this.refreshToken()) return true;

        // Show warning toast only if proactive refresh could not complete (e.g. offline)
        if (!this._hasShownExpirationWarning) {
          this.toastService.showToast(
            `Your session will expire in ${Math.max(
              1,
              Math.floor(timeUntilExpiration / 60),
            )} minutes. Please save your work.`,
            'warning',
          );
          this._hasShownExpirationWarning = true;
        }
      }

      return true;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      if (await this.refreshToken()) return true;
      this._isAuthenticated.next(false);
      this._userData = null;
      this.store.dispatch(UserActions.clearUserState());
      return false;
    }
  }

  /**
   * Initialize authentication state by validating the token.
   * Recovers session using refresh token if access token is expired or missing.
   */
  public async initializeAuthState(): Promise<void> {
    const authToken = await this.getToken();

    if (!authToken) {
      if (await this.refreshToken()) return;
      this._isAuthenticated.next(false);
      this._userData = null;
      this.store.dispatch(UserActions.clearUserState());
      return;
    }

    try {
      const decodedToken = jwtDecode<any>(authToken);
      const isTokenValid = !(decodedToken.exp < Date.now() / 1000);

      // If token is expired, recover session via refresh token before clearing
      if (!isTokenValid) {
        if (await this.refreshToken()) return;
        this._isAuthenticated.next(false);
        this._userData = null;
        this.store.dispatch(UserActions.clearUserState());
        this.clearAuthState();
        return;
      }

      this._isAuthenticated.next(true);
      this._userData = decodedToken;

      // If token is valid but approaching expiry, proactively refresh
      const timeUntilExpiration = decodedToken.exp - Date.now() / 1000;
      if (timeUntilExpiration <= this.TOKEN_EXPIRATION_WARNING_THRESHOLD) {
        await this.refreshToken();
      }
    } catch (error) {
      if (await this.refreshToken()) return;
      console.error('Error decoding token:', error);
      this._isAuthenticated.next(false);
      this._userData = null;
      this.store.dispatch(UserActions.clearUserState());
    }
  }

  /**
   * Get the authentication token
   * @returns The authentication token or null if not available
   */
  async getToken(): Promise<string | null> {
    return (
      (await this.storage.get(CONST.STORAGE_KEYS.ACCESS_TOKEN)) ||
      (await this.storage.get('__session'))
    );
  }

  /**
   * Get the refresh token
   * @returns The refresh token or null if not available
   */
  async getRefreshToken(): Promise<string | null> {
    return await this.storage.get(CONST.STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if user is authenticated
   * @returns boolean indicating if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    // Check token expiration before returning authentication state
    if (this._isAuthenticated.value) {
      return await this.checkTokenExpiration();
    }
    return false;
  }

  /**
   * Get the decoded JWT token
   * @returns decoded token or null if not valid
   */
  async getDecodedToken(): Promise<any> {
    // Check token expiration before returning user data
    if (this._userData && (await this.checkTokenExpiration())) {
      return this._userData;
    }
    return null;
  }

  /**
   * Check if user has required role
   * @param allowedRoles list of roles that can access
   * @returns boolean indicating if user has required role
   */
  async hasRequiredRole(
    allowedRoles: string[] = [UserRoles.ADMIN]
  ): Promise<boolean> {
    if (!this._userData || !(await this.checkTokenExpiration())) {
      return false;
    }

    return allowedRoles.includes(this._userData.user_role);
  }

  /**
   * Check if user is verified
   * @returns boolean indicating if user is verified
   */
  async isUserVerified(): Promise<boolean> {
    if (!this._userData || !(await this.checkTokenExpiration())) {
      return false;
    }

    return this._userData.user_status === UserStatus.VERIFIED;
  }

  /**
   * Set authentication state and user data
   * This should be called after successful login
   * @param token JWT token
   * @param refreshToken Optional refresh token
   * @param silent If true, don't show login toast (for background refresh)
   */
  setAuthState(token: string, refreshToken?: string, silent = false): void {
    if (!token) {
      this._isAuthenticated.next(false);
      this._userData = null;
      return;
    }

    try {
      const decodedToken = jwtDecode<any>(token);
      const isTokenValid = !(decodedToken.exp < Date.now() / 1000);

      this._isAuthenticated.next(isTokenValid);
      this._userData = isTokenValid ? decodedToken : null;

      // Store token in cookie with expiration (persist across browser restarts)
      this.storage.set(CONST.STORAGE_KEYS.ACCESS_TOKEN, token, true, {
        secure: true,
      });

      // Store persistent cookie for firebase hosting with expiration
      this.storage.set('__session', token, true, {
        secure: true,
      });

      // Store refresh token if provided with 30-day expiration
      if (refreshToken) {
        this.storage.set(CONST.STORAGE_KEYS.REFRESH_TOKEN, refreshToken, true, {
          secure: true,
          expires: 30, // 30 days to match backend TTL
        });
      }

      // Start token expiration check
      this.startTokenExpirationCheck();

      // Reset the warning flag
      this._hasShownExpirationWarning = false;

      // Show login success notification only if not silent
      if (!silent) {
        this.toastService.showToast(
          'You have successfully logged in.',
          'success'
        );
      }
    } catch (error) {
      this._isAuthenticated.next(false);
      this._userData = null;
    }
  }

  /**
   * Clear authentication state
   * This should be called after logout
   */
  clearAuthState(): void {
    this._isAuthenticated.next(false);
    this._userData = null;
    // Remove stored tokens
    this.storage.remove(CONST.STORAGE_KEYS.ACCESS_TOKEN);
    this.storage.remove(CONST.STORAGE_KEYS.REFRESH_TOKEN);
    this.storage.remove('__session');

    // Stop token expiration check
    if (this._tokenCheckSubscription) {
      this._tokenCheckSubscription.unsubscribe();
      this._tokenCheckSubscription = null;
    }

    // Reset the warning flag
    this._hasShownExpirationWarning = false;

    // Show logout notification
    this.toastService.showToast('You have been logged out.', 'info');
  }

  /**
   * Redirect to login page and save return URL
   * @param returnUrl URL to return to after login
   */
  redirectToLogin(returnUrl: string): void {
    this.storage.set(CONST.STORAGE_KEYS.RETURN_URL, returnUrl);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Redirect to home page
   */
  redirectToHome(): void {
    this.router.navigate(['/']);
  }

  /**
   * Redirect to verify user page
   */
  redirectToVerifyUser(): void {
    this.router.navigate(['/auth/verify-user']);
  }

  /**
   * Redirect to pending consent page
   */
  redirectToPendingConsent(): void {
    this.router.navigate(['/auth/pending-consent']);
  }
}
